/**
 * SchemaToolView — Schema Conversion Tool main layout.
 *
 * 5-panel layout inspired by AWS SCT:
 *   - Toolbar (source upload, target connection/dialect, convert/deploy buttons)
 *   - Source tree (left)
 *   - Assessment (center)
 *   - Target tree (right)
 *   - SQL preview (bottom, split source/target)
 *
 * No new backend — reuses ETL file server (port 8600) for connections + SQL execution.
 */

import { useState, useRef, useCallback } from 'react'
import {
  ArrowLeft, Upload, RefreshCw, Play, Download, Database,
  Loader2, CheckCircle, Plug, CheckSquare, Square, SlidersHorizontal
} from 'lucide-react'
import JSZip from 'jszip'
import SchemaSourceTree from './SchemaSourceTree'
import SchemaTargetTree from './SchemaTargetTree'
import SchemaAssessment from './SchemaAssessment'
import SchemaSqlPreview from './SchemaSqlPreview'
import SchemaDeployDialog from './SchemaDeployDialog'
import SchemaContextMenu from './SchemaContextMenu'
import SchemaTypeMappingDialog from './SchemaTypeMappingDialog'
import ConnectionManagerModal from '../etl/ConnectionManagerModal'
import { parseSchema, parseSchemaFiles, detectDialect } from '../../data/schemaParser'
import { convertSchema } from '../../data/schemaConverter'
import { deploySchema, testTargetConnection, exportAsSql } from '../../data/schemaDeployer'
import { listConnections } from '../../data/etlFileService'
import './SchemaToolView.css'

const DIALECTS = [
  { value: 'db2', label: 'DB2' },
  { value: 'sqlserver', label: 'SQL Server' },
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'oracle', label: 'Oracle' },
  { value: 'mysql', label: 'MySQL' },
]

function SchemaToolView({ onBack, onOpenSettings, onOpenHelp, userProfile }) {
  // Source state
  const [sourceFile, setSourceFile] = useState(null)
  const [sourceDialect, setSourceDialect] = useState('')
  const [parsedSchema, setParsedSchema] = useState(null)
  const [parsing, setParsing] = useState(false)

  // Target state
  const [targetDialect, setTargetDialect] = useState('db2')
  const [connections, setConnections] = useState([])
  const [selectedConnectionId, setSelectedConnectionId] = useState('')
  const [selectedConnection, setSelectedConnection] = useState(null)
  const [connectionLoaded, setConnectionLoaded] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState(null) // null | 'testing' | 'ok' | 'error'

  // Conversion state
  const [convertedSchema, setConvertedSchema] = useState(null)
  const [converting, setConverting] = useState(false)

  // Selection state (which object is selected in either tree)
  const [selectedObject, setSelectedObject] = useState(null)

  // Checked objects (for selective convert/deploy) — Set of "type:name" keys
  const [checkedObjects, setCheckedObjects] = useState(new Set())

  // Deploy dialog
  const [showDeployDialog, setShowDeployDialog] = useState(false)

  // Connection manager
  const [showConnectionManager, setShowConnectionManager] = useState(false)

  // Context menu
  const [contextMenu, setContextMenu] = useState(null) // { x, y, tree, type, name, data }

  // Type mapping rules
  const [typeMappingRules, setTypeMappingRules] = useState([])
  const [showTypeMappingDialog, setShowTypeMappingDialog] = useState(false)

  // File input ref
  const fileInputRef = useRef(null)

  // ── Load connections from ETL file server ──
  const loadConnections = useCallback(async () => {
    try {
      const conns = await listConnections()
      setConnections(conns || [])
      setConnectionLoaded(true)
    } catch (err) {
      console.error('[SchemaToolView] Failed to load connections:', err)
      setConnections([])
      setConnectionLoaded(true)
    }
  }, [])

  // Load connections on first render
  useState(() => {
    loadConnections()
  })

  // ── Checkbox helpers ──
  const checkAllFromSchema = (schema) => {
    const all = new Set()
    for (const t of schema.tables) all.add(`table:${t.name}`)
    for (const p of schema.procedures) all.add(`procedure:${p.name}`)
    for (const v of schema.views) all.add(`view:${v.name}`)
    for (const i of schema.indexes) all.add(`index:${i.name}`)
    setCheckedObjects(all)
  }

  const handleToggleCheck = (type, name) => {
    setCheckedObjects(prev => {
      const next = new Set(prev)
      const key = `${type}:${name}`
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleToggleAllCategory = (category, items, checked) => {
    setCheckedObjects(prev => {
      const next = new Set(prev)
      for (const item of items) {
        const key = `${category}:${item.name}`
        if (checked) next.add(key)
        else next.delete(key)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (parsedSchema) checkAllFromSchema(parsedSchema)
  }

  const handleDeselectAll = () => {
    setCheckedObjects(new Set())
  }

  // ── Handle file upload ──
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setParsing(true)
    setSourceFile(file.name)
    setParsedSchema(null)
    setConvertedSchema(null)
    setSelectedObject(null)
    setCheckedObjects(new Set())

    try {
      if (file.name.endsWith('.zip')) {
        // Read zip file
        const arrayBuffer = await file.arrayBuffer()
        const zip = await JSZip.loadAsync(arrayBuffer)

        // Extract all .sql files
        const files = []
        for (const [path, entry] of Object.entries(zip.files)) {
          if (entry.dir) continue
          if (!path.endsWith('.sql')) continue
          const content = await entry.async('string')
          const name = path.split('/').pop()
          files.push({ name, path, content })
        }

        const schema = parseSchemaFiles(files)
        setParsedSchema(schema)
        setSourceDialect(schema.dialect)
        checkAllFromSchema(schema) // auto-check all on upload
      } else if (file.name.endsWith('.sql')) {
        // Single SQL file
        const text = await file.text()
        const schema = parseSchema(text)
        setParsedSchema(schema)
        setSourceDialect(schema.dialect)
        checkAllFromSchema(schema) // auto-check all on upload
      }
    } catch (err) {
      console.error('[SchemaToolView] Parse error:', err)
    }

    setParsing(false)
    // Reset file input so same file can be re-uploaded
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // ── Handle connection selection ──
  const handleConnectionChange = (e) => {
    const connId = e.target.value
    setSelectedConnectionId(connId)
    setConnectionStatus(null)

    const conn = connections.find(c => c.id === connId || c.name === connId)
    setSelectedConnection(conn || null)

    // Auto-detect dialect from connection driver
    if (conn) {
      const driver = (conn.driverId || conn.driver || '').toLowerCase()
      if (driver.includes('db2')) setTargetDialect('db2')
      else if (driver.includes('sqlserver') || driver.includes('mssql')) setTargetDialect('sqlserver')
      else if (driver.includes('postgres')) setTargetDialect('postgresql')
      else if (driver.includes('oracle')) setTargetDialect('oracle')
      else if (driver.includes('mysql') || driver.includes('maria')) setTargetDialect('mysql')
    }
  }

  // ── Test connection ──
  const handleTestConnection = async () => {
    if (!selectedConnection) return
    setConnectionStatus('testing')
    try {
      const result = await testTargetConnection(selectedConnection)
      setConnectionStatus(result?.success ? 'ok' : 'error')
    } catch {
      setConnectionStatus('error')
    }
  }

  // ── Convert schema ──
  const handleConvert = () => {
    if (!parsedSchema) return
    setConverting(true)
    setSelectedObject(null)

    // Run conversion (synchronous, pure JS) — pass checked objects for selective conversion
    try {
      const result = convertSchema(parsedSchema, sourceDialect, targetDialect, checkedObjects.size > 0 ? checkedObjects : null, typeMappingRules)
      setConvertedSchema(result)
    } catch (err) {
      console.error('[SchemaToolView] Conversion error:', err)
    }

    setConverting(false)
  }

  // Compute checked counts for toolbar display
  const totalObjects = parsedSchema
    ? parsedSchema.tables.length + parsedSchema.procedures.length +
      (parsedSchema.views?.length || 0) + (parsedSchema.indexes?.length || 0)
    : 0
  const checkedCount = checkedObjects.size

  // ── Handle object selection ──
  const handleSelectObject = (obj) => {
    setSelectedObject(obj)
  }

  // ── Handle action item click ──
  const handleActionItemClick = (item) => {
    // Select the problematic object in the source tree
    const table = parsedSchema?.tables.find(t => t.name === item.object)
    if (table) {
      setSelectedObject({ type: 'table', name: table.name, data: table })
    }
  }

  // ── Handle target DDL edit ──
  const handleEditTargetDdl = (type, name, newDdl) => {
    if (!convertedSchema) return
    setConvertedSchema(prev => {
      const updated = { ...prev }
      if (type === 'table') {
        updated.tables = prev.tables.map(t =>
          t.name === name ? { ...t, convertedDdl: newDdl } : t
        )
      } else if (type === 'procedure') {
        updated.procedures = prev.procedures.map(p =>
          p.name === name ? { ...p, convertedDdl: newDdl } : p
        )
      }
      return updated
    })
  }

  // ── Deploy ──
  const handleDeploy = async (options, onProgress) => {
    if (!convertedSchema || !selectedConnection) return null
    return deploySchema(convertedSchema, selectedConnection, options, onProgress)
  }

  // ── Export as SQL ──
  const handleExport = () => {
    if (!convertedSchema) return
    const sql = exportAsSql(convertedSchema)
    const blob = new Blob([sql], { type: 'text/sql' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `schema_${targetDialect}_${new Date().toISOString().slice(0, 10)}.sql`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Context menu handler ──
  const handleContextMenu = (e, tree, type, name, data) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, tree, type, name, data })
  }

  const handleContextAction = (actionId) => {
    if (!contextMenu) return
    const { tree, type, name, data } = contextMenu

    switch (actionId) {
      case 'convert': {
        // Convert just this one object
        if (!parsedSchema) return
        const singleSet = new Set([`${type}:${name}`])
        try {
          const result = convertSchema(parsedSchema, sourceDialect, targetDialect, singleSet)
          setConvertedSchema(result)
        } catch (err) {
          console.error('[SchemaToolView] Single-object conversion error:', err)
        }
        break
      }
      case 'deploy': {
        // Deploy just this one object — select it, open deploy dialog
        const singleSet = new Set([`${type}:${name}`])
        setCheckedObjects(singleSet)
        setShowDeployDialog(true)
        break
      }
      case 'view-ddl':
      case 'edit-ddl':
        setSelectedObject({ type, name, data })
        break
      case 'copy-ddl': {
        const ddl = tree === 'target'
          ? (data?.convertedDdl || data?.rawDdl || '')
          : (data?.rawDdl || '')
        navigator.clipboard.writeText(ddl).catch(() => {})
        break
      }
      case 'export-sql': {
        const ddl = data?.convertedDdl || data?.rawDdl || ''
        const blob = new Blob([ddl], { type: 'text/sql' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${name}.sql`
        a.click()
        URL.revokeObjectURL(url)
        break
      }
      case 'select-all-category': {
        const items = type === 'table' ? (parsedSchema?.tables || [])
          : type === 'procedure' ? (parsedSchema?.procedures || [])
          : type === 'view' ? (parsedSchema?.views || [])
          : (parsedSchema?.indexes || [])
        handleToggleAllCategory(type, items, true)
        break
      }
      case 'deselect-all-category': {
        const items2 = type === 'table' ? (parsedSchema?.tables || [])
          : type === 'procedure' ? (parsedSchema?.procedures || [])
          : type === 'view' ? (parsedSchema?.views || [])
          : (parsedSchema?.indexes || [])
        handleToggleAllCategory(type, items2, false)
        break
      }
    }
    setContextMenu(null)
  }

  // ── Connection Manager callbacks ──
  const handleConnectionManagerClose = () => {
    setShowConnectionManager(false)
    loadConnections() // refresh list after changes
  }

  return (
    <div className="schema-tool-view">
      {/* ── Toolbar ── */}
      <div className="schema-toolbar">
        <button className="schema-toolbar-btn" onClick={onBack} title="Back">
          <ArrowLeft size={16} />
        </button>

        <div className="schema-toolbar-title">Schema Conversion Tool</div>

        <div className="schema-toolbar-divider" />

        {/* Source file upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip,.sql"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
        <button
          className="schema-toolbar-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={parsing}
        >
          {parsing ? <Loader2 size={14} className="schema-spinner" /> : <Upload size={14} />}
          {sourceFile ? sourceFile : 'Upload DDL'}
        </button>

        {sourceDialect && (
          <span className="schema-toolbar-dialect-badge">
            Source: {sourceDialect.toUpperCase()}
          </span>
        )}

        <div className="schema-toolbar-divider" />

        {/* Target connection */}
        <select
          className="schema-toolbar-select"
          value={selectedConnectionId}
          onChange={handleConnectionChange}
        >
          <option value="">Select connection...</option>
          {connections.map(conn => (
            <option key={conn.id || conn.name} value={conn.id || conn.name}>
              {conn.name}
            </option>
          ))}
        </select>

        <button
          className="schema-toolbar-btn schema-toolbar-btn-sm"
          onClick={() => setShowConnectionManager(true)}
          title="Manage Connections"
        >
          <Plug size={14} />
        </button>

        {selectedConnection && (
          <button
            className="schema-toolbar-btn schema-toolbar-btn-sm"
            onClick={handleTestConnection}
            disabled={connectionStatus === 'testing'}
            title="Test Connection"
          >
            {connectionStatus === 'testing' ? (
              <Loader2 size={14} className="schema-spinner" />
            ) : connectionStatus === 'ok' ? (
              <CheckCircle size={14} className="schema-status-ok" />
            ) : (
              <Database size={14} />
            )}
          </button>
        )}

        {/* Target dialect */}
        <select
          className="schema-toolbar-select"
          value={targetDialect}
          onChange={e => setTargetDialect(e.target.value)}
        >
          {DIALECTS.map(d => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>

        {/* Type mapping rules */}
        <button
          className="schema-toolbar-btn schema-toolbar-btn-sm"
          onClick={() => setShowTypeMappingDialog(true)}
          title="Data Type Mapping Rules"
        >
          <SlidersHorizontal size={14} />
          {typeMappingRules.length > 0 && (
            <span className="schema-toolbar-rules-badge">{typeMappingRules.length}</span>
          )}
        </button>

        {/* Selection controls */}
        {parsedSchema && (
          <>
            <div className="schema-toolbar-divider" />
            <span className="schema-toolbar-selection-count">
              {checkedCount} / {totalObjects}
            </span>
            <button
              className="schema-toolbar-btn schema-toolbar-btn-sm"
              onClick={handleSelectAll}
              title="Select All"
            >
              <CheckSquare size={14} />
            </button>
            <button
              className="schema-toolbar-btn schema-toolbar-btn-sm"
              onClick={handleDeselectAll}
              title="Deselect All"
            >
              <Square size={14} />
            </button>
          </>
        )}

        <div className="schema-toolbar-spacer" />

        {/* Convert button */}
        <button
          className="schema-toolbar-btn schema-btn-primary"
          onClick={handleConvert}
          disabled={!parsedSchema || converting}
        >
          {converting ? <Loader2 size={14} className="schema-spinner" /> : <RefreshCw size={14} />}
          Convert
        </button>

        {/* Deploy button */}
        <button
          className="schema-toolbar-btn schema-btn-deploy"
          onClick={() => setShowDeployDialog(true)}
          disabled={!convertedSchema || !selectedConnection}
        >
          <Play size={14} />
          Deploy
        </button>

        {/* Export button */}
        <button
          className="schema-toolbar-btn"
          onClick={handleExport}
          disabled={!convertedSchema}
        >
          <Download size={14} />
          Export
        </button>
      </div>

      {/* ── Main content: 3 panels + bottom SQL ── */}
      <div className="schema-main">
        {/* Top row: Source tree | Assessment | Target tree */}
        <div className="schema-panels-top">
          <div className="schema-panel schema-panel-source">
            <div className="schema-panel-header">Source Objects</div>
            <SchemaSourceTree
              schema={parsedSchema}
              selectedObject={selectedObject}
              onSelectObject={handleSelectObject}
              checkedObjects={checkedObjects}
              onToggleCheck={handleToggleCheck}
              onToggleAllCategory={handleToggleAllCategory}
              onContextMenu={(e, type, name, data) => handleContextMenu(e, 'source', type, name, data)}
            />
          </div>

          <div className="schema-panel schema-panel-assessment">
            <SchemaAssessment
              assessment={convertedSchema?.assessment}
              onSelectActionItem={handleActionItemClick}
            />
          </div>

          <div className="schema-panel schema-panel-target">
            <div className="schema-panel-header">Target Objects</div>
            <SchemaTargetTree
              convertedSchema={convertedSchema}
              selectedObject={selectedObject}
              onSelectObject={handleSelectObject}
              actionItems={convertedSchema?.assessment?.actionItems}
              checkedObjects={checkedObjects}
              onToggleCheck={handleToggleCheck}
              onToggleAllCategory={handleToggleAllCategory}
              onContextMenu={(e, type, name, data) => handleContextMenu(e, 'target', type, name, data)}
            />
          </div>
        </div>

        {/* Bottom row: SQL preview */}
        <div className="schema-panels-bottom">
          <SchemaSqlPreview
            selectedObject={selectedObject}
            convertedSchema={convertedSchema}
            onEditTargetDdl={handleEditTargetDdl}
          />
        </div>
      </div>

      {/* Deploy dialog */}
      <SchemaDeployDialog
        isOpen={showDeployDialog}
        onClose={() => setShowDeployDialog(false)}
        convertedSchema={convertedSchema}
        connection={selectedConnection}
        targetDialect={targetDialect}
        onDeploy={handleDeploy}
        checkedObjects={checkedObjects}
      />

      {/* Type mapping rules dialog */}
      <SchemaTypeMappingDialog
        isOpen={showTypeMappingDialog}
        onClose={() => setShowTypeMappingDialog(false)}
        rules={typeMappingRules}
        onRulesChange={setTypeMappingRules}
        actionItems={convertedSchema?.assessment?.actionItems || []}
      />

      {/* Context menu */}
      {contextMenu && (
        <SchemaContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          tree={contextMenu.tree}
          objectType={contextMenu.type}
          objectName={contextMenu.name}
          onSelect={handleContextAction}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Connection Manager */}
      {showConnectionManager && (
        <ConnectionManagerModal
          isOpen={showConnectionManager}
          onClose={handleConnectionManagerClose}
          initialConnectionId={selectedConnectionId || null}
          initialDriverId={null}
          onApply={(connId) => {
            // Refresh connections list and select the applied connection
            loadConnections().then(() => {
              if (connId) setSelectedConnectionId(connId)
            })
            setShowConnectionManager(false)
          }}
        />
      )}
    </div>
  )
}

export default SchemaToolView
